import {
    IMAGERY_LAYER,
    IMAGERY_SOURCE,
    SI_SOURCE,
    SI_FILL_LAYER,
    WATER_LAYER,
    WATER_SOURCE,
} from "@/lib/map/layers";
import SimpleLayer from "./SimpleLayer";
import { Select, SelectItem, Skeleton } from "@heroui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useMap from "@/lib/map/useMap";
import { useParams } from "@tanstack/react-router";
import { Field } from "@/types/field";
import useRequest from "@/lib/request/useRequest";
import { useQuery } from "@tanstack/react-query";
import { ImageSource } from "mapbox-gl";
import {
    ImageryDetails,
    ImageryType,
    ImageAnalytics,
    isWaterImageryType,
} from "@/types/imagery";
import { roundTo } from "@/lib/utils/numbers";
import dayjs from "dayjs";
import {
    removeLayersIfExists,
    removeSourcesIfExists,
    invertedTurfBboxToFourCornerCoordinates,
    Coordinates,
    addUpdateGeojsonSource,
    addUpdateGeojsonLayers,
    updateLayerVisibility,
    moveLayersToTop,
} from "@/lib/utils/map";
import { calculateYBands } from "@/lib/utils/sensitivity";
import { FeatureCollection, Feature, MultiPolygon } from "geojson";
import { useI18n } from "@/lib/i18n/useI18n";
import { WaterRiskZones } from "@/types/water";

function ImageryLayer() {
    const {
        imageryBbox,
        selectedImage,
        imageType,
        setImageType,
        imageryOptions,
        selectedDate,
    } = useFieldDashboard() ?? {};
    const { map, mapLoaded } = useMap();
    const { useGetRequest, axiosRequest, downloadFile } = useRequest();

    const imageryDetails = useGetRequest<ImageryDetails>({
        url: `imagery/${selectedImage?.id}/`,
        enabled: !!selectedImage,
    });
    const bbox = useMemo(() => {
        if (imageryDetails.data?.bounds?.length) {
            return invertedTurfBboxToFourCornerCoordinates(
                imageryDetails.data?.bounds,
            ) as Coordinates;
        } else {
            return imageryBbox;
        }
    }, [imageryDetails.data?.bounds, imageryBbox]);

    const { fieldId } = useParams({
        strict: false,
    });

    const fieldQuery = useGetRequest<Field>({
        url: `fields/${fieldId}/`,
        enabled: !!fieldId,
    });

    const imageUrl = useMemo(() => {
        switch (imageType) {
            case ImageryType.NDRE:
                return imageryDetails.data?.display_urls?.ndre;
            case ImageryType.NDVI:
                return imageryDetails.data?.display_urls?.ndvi;
            case ImageryType.RGB:
                return imageryDetails.data?.display_urls?.rgb;
            case ImageryType.SAVI:
                return imageryDetails.data?.display_urls?.savi;
            case ImageryType.OSAVI:
                return imageryDetails.data?.display_urls?.osavi;
            case ImageryType.CCCI:
                return imageryDetails.data?.display_urls?.ccci;
            default:
                return imageryDetails.data?.display_urls?.ndre;
        }
    }, [imageType, imageryDetails.data]);

    const [forecastType, setForecastType] = useState<
        "past_7day" | "past_1day" | "future_7day"
    >("past_1day");
    const [cropWaterUseType, setCropWaterUseType] = useState<
        "past_7day" | "past_3day" | "future_3day" | "future_7day"
    >("past_7day");

    const fieldSoilWaterContentQuery = useGetRequest<WaterRiskZones>({
        url: `fields/${fieldId}/water_risk_zones/`,
        enabled: !!fieldId && imageType === ImageryType.SOIL_WATER_CONTENT,
    });

    const soilWaterSteps = useMemo(() => {
        if (!fieldSoilWaterContentQuery.data?.data) return [];
        const data = fieldSoilWaterContentQuery.data?.data?.find((d) =>
            dayjs(d.time).isSame(selectedDate, "day"),
        );
        if (!data) return [];
        const [min, max] = [data.rtsml_b, data.rtsml_t];
        const difference = (max - min) / 2;
        return [
            roundTo(min - difference),
            roundTo(min),
            roundTo(min + difference),
            roundTo(max),
            roundTo(max + difference),
        ];
    }, [fieldSoilWaterContentQuery.data?.data, selectedDate]);

    const waterQuery = useQuery({
        queryKey: [
            "field",
            fieldId,
            "water_map_image",
            imageType,
            forecastType,
            cropWaterUseType,
            selectedDate,
            ...soilWaterSteps,
        ],
        queryFn: () =>
            axiosRequest({
                url: `fields/${fieldId}/water_map_image/`,
                responseType: "blob",
                params: {
                    forecast_type: forecastType,
                    crop_water_use_type: cropWaterUseType,
                    image_type: imageType,
                    color_steps: soilWaterSteps,
                    target_date: selectedDate,
                },
                paramsSerializer: {
                    indexes: null,
                },
            }),
        enabled:
            !!selectedDate &&
            isWaterImageryType(imageType) &&
            soilWaterSteps.length > 0,
    });
    const waterBounds = useQuery<{
        bounds: Coordinates;
        min_value: number;
        max_value: number;
    }>({
        queryKey: [
            "field",
            fieldId,
            "water_map_metadata",
            imageType,
            forecastType,
            cropWaterUseType,
            selectedDate,
            ...soilWaterSteps,
        ],
        queryFn: () =>
            axiosRequest({
                url: `fields/${fieldId}/water_map_metadata/`,
                params: {
                    forecast_type: forecastType,
                    crop_water_use_type: cropWaterUseType,
                    image_type: imageType,
                    target_date: dayjs(selectedDate).format(
                        "YYYY-MM-DD",
                    ),
                    color_steps: soilWaterSteps,
                },
                paramsSerializer: {
                    indexes: null,
                },
            }),
        enabled:
            !!selectedDate &&
            isWaterImageryType(imageType) &&
            soilWaterSteps.length > 0,
    });
    const waterBbox = useMemo(() => {
        if (waterBounds.data?.bounds) {
            return waterBounds.data.bounds;
        }
        return undefined;
    }, [waterBounds.data]);
    const [waterUrl, setWaterUrl] = useState<string | null>(null);
    useEffect(() => {
        if (!waterQuery.data) return;
        const url = URL.createObjectURL(waterQuery.data as Blob);
        setWaterUrl(url);

        return () => {
            URL.revokeObjectURL(url);
        };
    }, [waterQuery.data]);
    useEffect(() => {
        if (!map || !mapLoaded) return;
        if (waterBbox && waterUrl && isWaterImageryType(imageType)) {
            const im = map.getSource(WATER_SOURCE) as ImageSource;
            if (im) {
                if (im.url !== waterUrl) {
                    im.updateImage({
                        url: waterUrl,
                        coordinates: waterBbox,
                    });
                }
            } else {
                map.addSource(WATER_SOURCE, {
                    type: "image",
                    url: waterUrl,
                    coordinates: waterBbox,
                });
                map.addLayer({
                    id: WATER_LAYER,
                    type: "raster",
                    source: WATER_SOURCE,
                    paint: {
                        "raster-fade-duration": 0,
                    },
                });
            }
        } else {
            removeLayersIfExists(map, [WATER_LAYER]);
            removeSourcesIfExists(map, [WATER_SOURCE]);
        }
    }, [waterBbox, map, mapLoaded, imageType, waterUrl, forecastType]);

    const bands = useMemo(
        () =>
            calculateYBands(
                fieldQuery?.data?.insight_sensitivity ?? "moderate",
            ),
        [fieldQuery?.data?.insight_sensitivity],
    );

    const imageAnalyticsQuery = useGetRequest<ImageAnalytics>({
        url: `imagery/${selectedImage?.id}/analytics/`,
        enabled: !!selectedImage?.id,
    });

    const siMap = useMemo(() => {
        if (!imageAnalyticsQuery?.data?.features?.length) return null;
        const zones: number[] = [
            ...new Set(
                imageAnalyticsQuery.data.features.map(
                    (feature) => feature.properties.management_zone,
                ),
            ),
        ];
        return {
            type: "FeatureCollection",
            features: zones.map((zone) => {
                const zoneFeatures: Feature<MultiPolygon>[] =
                    imageAnalyticsQuery.data.features.filter(
                        (feature) =>
                            feature.properties.management_zone === zone,
                    );
                const zoneCanaries: Feature[] = zoneFeatures.filter(
                    (feature) => feature.properties?.plot_type === "Canary",
                );
                const siMean =
                    zoneCanaries.reduce(
                        (acc, feature) =>
                            acc + (feature.properties?.sensitized_si ?? 0),
                        0,
                    ) / zoneCanaries.length;
                const siColor = bands.find(
                    (band) => band.y0 < siMean * 100 && band.y1 >= siMean * 100,
                )?.mapColor;

                let combinedGeometry;
                if (zoneFeatures.length > 1) {
                    const polygons = zoneFeatures
                        .map((feature) => {
                            if (feature.geometry.type === "MultiPolygon") {
                                return feature.geometry.coordinates;
                            }
                            return [];
                        })
                        .flat();
                    combinedGeometry = {
                        type: "MultiPolygon",
                        coordinates: polygons,
                    };
                } else {
                    combinedGeometry = zoneFeatures[0]?.geometry;
                }

                return {
                    type: "Feature",
                    id: `zone-${zone}`,
                    properties: {
                        zone: zone,
                        si_mean: siMean,
                        color: siColor,
                    },
                    geometry: combinedGeometry,
                };
            }),
        } as FeatureCollection;
    }, [imageAnalyticsQuery?.data?.features, bands]);

    useEffect(() => {
        if (!map || !mapLoaded) return;
        if (
            bbox &&
            imageUrl &&
            imageType !== ImageryType.SI &&
            !isWaterImageryType(imageType)
        ) {
            const im = map.getSource(IMAGERY_SOURCE) as ImageSource;
            if (im) {
                if (im.url !== imageUrl) {
                    im.updateImage({
                        url: imageUrl,
                        coordinates: bbox,
                    });
                }
            } else {
                map.addSource(IMAGERY_SOURCE, {
                    type: "image",
                    url: imageUrl,
                    coordinates: bbox,
                });
                map.addLayer({
                    id: IMAGERY_LAYER,
                    type: "raster",
                    source: IMAGERY_SOURCE,
                    paint: {
                        "raster-fade-duration": 0,
                    },
                });
            }
        } else {
            removeLayersIfExists(map, [IMAGERY_LAYER]);
            removeSourcesIfExists(map, [IMAGERY_SOURCE]);
        }
        if (imageType === ImageryType.SI && siMap) {
            addUpdateGeojsonSource(map, SI_SOURCE, {
                type: "geojson",
                data: siMap,
                tolerance: 0,
            });
            addUpdateGeojsonLayers(map, [SI_FILL_LAYER]);
            updateLayerVisibility(map, [SI_FILL_LAYER.id], true);
            moveLayersToTop(map, [SI_FILL_LAYER.id]);
        } else {
            removeLayersIfExists(map, [SI_FILL_LAYER.id]);
            removeSourcesIfExists(map, [SI_SOURCE]);
        }
    }, [bbox, imageUrl, map, mapLoaded, imageType, siMap]);

    const [mean, max] = useMemo(() => {
        if (!imageryDetails.data) return [0, 0];
        if (imageType === ImageryType.NDRE) {
            return [
                imageryDetails.data?.ndre_mean,
                imageryDetails.data?.ndre_max,
            ];
        }
        if (imageType === ImageryType.NDVI) {
            return [
                imageryDetails.data?.ndvi_mean,
                imageryDetails.data?.ndvi_max,
            ];
        }
        return [0, 0];
    }, [imageType, imageryDetails.data]);

    const downloadImagery = useCallback(async () => {
        await downloadFile(
            `imagery/${selectedImage?.id}/download/`,
            `${fieldQuery?.data?.name} ${dayjs(imageryDetails?.data?.capture_date).format("YYYY-MM-DD")} Image.zip`,
        );
    }, [
        downloadFile,
        fieldQuery?.data?.name,
        imageryDetails?.data?.capture_date,
        selectedImage?.id,
    ]);

    const { formatUnit } = useI18n();

    return (
        <SimpleLayer
            name="Imagery"
            layerNames={[IMAGERY_LAYER, WATER_LAYER]}
            defaultVisible
            disabled={!selectedImage && !selectedDate}
            onDownload={downloadImagery}
        >
            {(isChecked) => {
                if (!isChecked) return null;
                return (
                    <div className="flex flex-col gap-1">
                        <Select
                            className="w-full mt-3"
                            aria-label="Select Imagery Type"
                            selectedKeys={
                                imageType &&
                                    imageryOptions &&
                                    imageryOptions.length > 0
                                    ? [imageType]
                                    : []
                            }
                            onChange={(key) =>
                                setImageType?.(key.target.value as ImageryType)
                            }
                            size="sm"
                        >
                            {(imageryOptions ?? []).map((option) => (
                                <SelectItem key={option.key}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </Select>
                        {(imageType === "NDVI" || imageType === "NDRE") && (
                            <div>
                                <div className="flex flex-col items-center mt-2">
                                    <div className="w-full h-4 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-md"></div>
                                    <div className="flex justify-between w-full mt-1 text-sm">
                                        <span>0</span>
                                        <span>{roundTo(mean, 2)}</span>
                                        <span>{roundTo(max, 2)}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {isWaterImageryType(imageType) && (
                            <div>
                                {soilWaterSteps?.length == 0 &&
                                    !fieldSoilWaterContentQuery.isLoading && (
                                        <div className="text-xs italic text-red-500">
                                            No data available for this date
                                        </div>
                                    )}
                                {waterBounds?.isLoading ? (
                                    <Skeleton className="h-8 w-full rounded-md" />
                                ) : (
                                    <>
                                        {imageType ===
                                            ImageryType.SOIL_WATER_CONTENT && (
                                                <div className="flex flex-col items-center mt-2">
                                                    <div className="w-full h-4 bg-gradient-to-r from-[#ADD8E6] to-[#0038A8] rounded-md"></div>
                                                    <div className="flex justify-between w-full mt-1 text-sm">
                                                        <span>
                                                            {roundTo(
                                                                waterBounds.data
                                                                    ?.min_value ??
                                                                0,
                                                                2,
                                                            )}{" "}
                                                            {formatUnit("in", "mm")}
                                                        </span>
                                                        <span>
                                                            {roundTo(
                                                                waterBounds.data
                                                                    ?.max_value ??
                                                                0,
                                                                2,
                                                            )}{" "}
                                                            {formatUnit("in", "mm")}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        {imageType ===
                                            ImageryType.LEACHING_RISK && (
                                                <div className="flex flex-col items-center mt-2">
                                                    <div className="w-full h-4 bg-gradient-to-r from-[#FFEB3B] to-[#2196F3] rounded-md"></div>
                                                    <div className="flex justify-between w-full mt-1 text-sm">
                                                        <span>0%</span>
                                                        <span>100%</span>
                                                    </div>
                                                </div>
                                            )}
                                        {imageType ===
                                            ImageryType.DROUGHT_RISK && (
                                                <div className="flex flex-col items-center mt-2">
                                                    <div
                                                        className="w-full h-4 rounded-md"
                                                        style={{
                                                            background:
                                                                "linear-gradient(to right, rgb(76,175,80), rgb(244,67,54))",
                                                        }}
                                                    ></div>
                                                    <div className="flex justify-between w-full mt-1 text-sm">
                                                        <span>0%</span>
                                                        <span>100%</span>
                                                    </div>
                                                </div>
                                            )}
                                        {imageType ===
                                            ImageryType.CROP_WATER_USE && (
                                                <div className="flex flex-col items-center mt-2">
                                                    <div
                                                        className="w-full h-4 rounded-md"
                                                        style={{
                                                            background:
                                                                "linear-gradient(to right, rgb(255,255,178), rgb(33,102,172))",
                                                        }}
                                                    ></div>
                                                    <div className="flex justify-between w-full mt-1 text-sm">
                                                        <span>
                                                            {roundTo(
                                                                waterBounds.data
                                                                    ?.min_value ??
                                                                0,
                                                                2,
                                                            )}{" "}
                                                            {formatUnit("in", "mm")}
                                                        </span>
                                                        <span>
                                                            {roundTo(
                                                                waterBounds.data
                                                                    ?.max_value ??
                                                                0,
                                                                2,
                                                            )}{" "}
                                                            {formatUnit("in", "mm")}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        {imageType ==
                                            ImageryType.CROP_WATER_USE ? (
                                            <Select
                                                label="Type"
                                                size="sm"
                                                labelPlacement="outside"
                                                selectedKeys={
                                                    cropWaterUseType
                                                        ? [cropWaterUseType]
                                                        : []
                                                }
                                                onChange={(key) =>
                                                    setCropWaterUseType(
                                                        key.target.value as
                                                        | "past_7day"
                                                        | "past_3day"
                                                        | "future_3day"
                                                        | "future_7day",
                                                    )
                                                }
                                            >
                                                <SelectItem key="past_7day">
                                                    Past 7 Days
                                                </SelectItem>
                                                <SelectItem key="past_3day">
                                                    Past 3 Days
                                                </SelectItem>
                                                <SelectItem key="future_3day">
                                                    Next 3 Days
                                                </SelectItem>
                                                <SelectItem key="future_7day">
                                                    Next 7 Days
                                                </SelectItem>
                                            </Select>
                                        ) : (
                                            <Select
                                                label="Type"
                                                size="sm"
                                                labelPlacement="outside"
                                                selectedKeys={
                                                    forecastType
                                                        ? [forecastType]
                                                        : []
                                                }
                                                onChange={(key) =>
                                                    setForecastType(
                                                        key.target.value as
                                                        | "past_7day"
                                                        | "past_1day"
                                                        | "future_7day",
                                                    )
                                                }
                                            >
                                                <SelectItem key="past_7day">
                                                    Last Week
                                                </SelectItem>
                                                <SelectItem key="past_1day">
                                                    Today
                                                </SelectItem>
                                                <SelectItem key="future_7day">
                                                    Forecast
                                                </SelectItem>
                                            </Select>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                );
            }}
        </SimpleLayer>
    );
}

export default ImageryLayer;
